// storage.js — Supabase-backed cross-device sync
// Falls back to localStorage if Supabase is unreachable

const SUPABASE_URL = "https://oocybkiuoemepxdeiagh.supabase.co";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im9vY3lia2l1b2VtZXB4ZGVpYWdoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQzOTIyODQsImV4cCI6MjA4OTk2ODI4NH0.CXntdp6_Z3b78eg1wojwOKgF4gIhxNL3O8e0-rApaD0";

const headers = {
  "Content-Type": "application/json",
  "apikey": SUPABASE_KEY,
  "Authorization": `Bearer ${SUPABASE_KEY}`,
  "Prefer": "resolution=merge-duplicates",
};

export async function load(key) {
  if (typeof window !== "undefined" && window.storage) {
    try {
      const r = await window.storage.get(key);
      return r ? JSON.parse(r.value) : null;
    } catch {}
  }

  // A failed remote write must remain authoritative on this device until it
  // can be synchronized. Otherwise a later load can resurrect stale cloud data.
  try {
    const pending = localStorage.getItem(`storage-pending:${key}`);
    if (pending !== null) {
      try {
        const sync = await fetch(`${SUPABASE_URL}/rest/v1/storage`, {
          method: "POST",
          headers,
          body: JSON.stringify({ key, value: pending }),
        });
        if (sync.ok) localStorage.removeItem(`storage-pending:${key}`);
      } catch {}
      return JSON.parse(pending);
    }
  } catch {}

  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/storage?key=eq.${encodeURIComponent(key)}&select=value`,
      { headers }
    );
    if (!res.ok) throw new Error("Supabase error");
    const data = await res.json();
    if (data && data.length > 0) {
      return JSON.parse(data[0].value);
    }
    return null;
  } catch {
    try {
      const val = localStorage.getItem(key);
      return val ? JSON.parse(val) : null;
    } catch {
      return null;
    }
  }
}

export async function save(key, val) {
  const value = JSON.stringify(val);

  if (typeof window !== "undefined" && window.storage) {
    try {
      await window.storage.set(key, value);
      return;
    } catch {}
  }

  try {
    const check = await fetch(`${SUPABASE_URL}/rest/v1/storage?key=eq.${encodeURIComponent(key)}&select=key`, { headers });
    if (!check.ok) throw new Error(`Storage lookup failed (${check.status})`);
    const existing = await check.json();
    const res = await fetch(existing.length
      ? `${SUPABASE_URL}/rest/v1/storage?key=eq.${encodeURIComponent(key)}`
      : `${SUPABASE_URL}/rest/v1/storage`, {
      method: existing.length ? "PATCH" : "POST",
      headers: { ...headers, "Prefer": "return=minimal" },
      body: JSON.stringify(existing.length ? { value } : { key, value }),
    });
    if (!res.ok) throw new Error(`Storage write failed (${res.status})`);
    try { localStorage.removeItem(`storage-pending:${key}`); } catch {}
  } catch {
    try { localStorage.setItem(`storage-pending:${key}`, value); } catch {}
  }

  try {
    localStorage.setItem(key, value);
  } catch {}
}

// Reliability-sensitive writes must be confirmed by shared persistence. A
// local pending copy is retained for recovery, but the rejected promise keeps
// callers from presenting the operation as approved.
export async function saveConfirmed(key, val) {
  const value = JSON.stringify(val);

  if (typeof window !== "undefined" && window.storage) {
    try {
      await window.storage.set(key, value);
      try { localStorage.setItem(key, value); } catch {}
      return;
    } catch {}
  }

  try {
    const check = await fetch(`${SUPABASE_URL}/rest/v1/storage?key=eq.${encodeURIComponent(key)}&select=key`, { headers });
    if (!check.ok) throw new Error(`Storage lookup failed (${check.status})`);
    const existing = await check.json();
    const res = await fetch(existing.length
      ? `${SUPABASE_URL}/rest/v1/storage?key=eq.${encodeURIComponent(key)}`
      : `${SUPABASE_URL}/rest/v1/storage`, {
      method: existing.length ? "PATCH" : "POST",
      headers: { ...headers, "Prefer": "return=minimal" },
      body: JSON.stringify(existing.length ? { value } : { key, value }),
    });
    if (!res.ok) throw new Error(`Storage write failed (${res.status})`);
    try {
      localStorage.removeItem(`storage-pending:${key}`);
      localStorage.setItem(key, value);
    } catch {}
  } catch (error) {
    try { localStorage.setItem(`storage-pending:${key}`, value); } catch {}
    const failure = new Error(`Shared persistence was not confirmed for ${key}: ${error.message}`);
    failure.code = "SHARED_PERSISTENCE_FAILED";
    failure.cause = error;
    throw failure;
  }
}
